/**
 * El recorrido del paso "Estrategia de Meta Ads": las tres pantallas por las
 * que pasa y qué las conecta.
 *
 * POR QUÉ ES UN MÓDULO Y NO TRES `useState` EN EL COMPONENTE. El paso tiene una
 * pregunta que RAMIFICA ("¿ya venías haciendo Meta Ads?") y dos formas de
 * terminar (contándolo o delegándolo). Con banderas sueltas —`respondio`,
 * `delego`, `texto`— existen combinaciones que no significan nada ("delegó pero
 * además escribió", "escribió sin haber contestado") y la pantalla termina
 * decidiendo cuál gana en un ternario. Acá el estado es UNA cosa con tres
 * formas posibles: no hay combinación inválida que dibujar.
 *
 * Lógica pura, sin React: se testea sin montar nada.
 *
 * Ver ADR-2026-08-18-estudio-marketing-paso-estrategia-meta-ads.
 */

/** Cómo terminó el paso: lo contó el usuario o lo delegó en Atlas. */
export type ViaEstrategia = "contado" | "delegado";

export type MomentoMetaAds =
  /** La pregunta que ramifica. Es donde arranca siempre. */
  | { paso: "pregunta" }
  /** El cuadro para contar cómo se venía trabajando. Sólo se llega diciendo
   *  que SÍ: a quien nunca hizo Meta Ads no hay nada que preguntarle. */
  | { paso: "contame" }
  /** Resuelto. `relato` es lo que escribió el usuario, o `null` si delegó. */
  | { paso: "listo"; via: ViaEstrategia; relato: string | null };

export const MOMENTO_INICIAL: MomentoMetaAds = { paso: "pregunta" };

/** Delegar es el mismo desenlace se llegue de donde se llegue: por el botón
 *  grande o por haber contestado que nunca hizo Meta Ads. */
export const MOMENTO_DELEGADO: MomentoMetaAds = { paso: "listo", via: "delegado", relato: null };

/**
 * A dónde manda la respuesta.
 *
 * Es LA razón de ser de la pregunta: el "no" se saltea el cuadro entero. Pedirle
 * a alguien que nunca hizo Meta Ads que cuente cómo lo venía trabajando es
 * pedirle que invente algo, y eso después se lee como si fuera historia real.
 */
export function momentoSegunRespuesta(yaHizo: boolean): MomentoMetaAds {
  return yaHizo ? { paso: "contame" } : MOMENTO_DELEGADO;
}

/** Los saltos de línea de Windows cuentan doble y ensucian el conteo. Mismo
 *  criterio que `materialesConocimiento.ts`. */
export const normalizarRelato = (crudo: string) => crudo.replace(/\r\n?/g, "\n").trim();

/** Con el cuadro vacío no hay nada que mandar. Es lo que apaga el botón de
 *  enviar, igual que en la barra del chat. */
export const puedeEnviarRelato = (crudo: string) => normalizarRelato(crudo).length > 0;

/**
 * El momento "listo" con lo que escribió el usuario, o `null` si no escribió
 * nada. El `null` no se dibuja: es la señal de que no hay que avanzar.
 */
export function momentoContado(crudo: string): MomentoMetaAds | null {
  const relato = normalizarRelato(crudo);
  if (!relato) return null;
  return { paso: "listo", via: "contado", relato };
}

export interface ResumenMetaAds {
  titulo: string;
  detalle: string;
  /** Qué dice el botón que deshace la decisión. */
  textoDeshacer: string;
}

/**
 * Lo que dice la pantalla resuelta. Las dos vías NO se cuentan igual: una tiene
 * un texto del usuario detrás y la otra no, y prometer "ya tengo tu contexto"
 * cuando el usuario no contó nada sería inventarle un contexto que no existe.
 *
 * Devuelve `null` en los momentos que no son "listo": no hay resumen de algo
 * que todavía no se decidió.
 */
export function resumenDeMomento(momento: MomentoMetaAds): ResumenMetaAds | null {
  if (momento.paso !== "listo") return null;
  if (momento.via === "contado") {
    return {
      titulo: "Listo, ya tengo tu contexto",
      detalle: "Con esto Atlas arma la estrategia de Meta Ads.",
      // Vuelve al cuadro, no a la pregunta: lo que se quiere corregir es el
      // texto, y mandarlo de nuevo a "¿ya hiciste Meta Ads?" le borraría lo
      // que escribió para volver al mismo lugar.
      textoDeshacer: "Editar lo que escribí",
    };
  }
  return {
    titulo: "Atlas arma la estrategia",
    detalle:
      "Va a proponerte una estrategia de Meta Ads con lo que sabe de tu marca, tus fuentes y tu sistema de diseño.",
    textoDeshacer: "Prefiero contarte yo",
  };
}

/**
 * A dónde vuelve el botón que deshace. Delegar se deshace volviendo a la
 * PREGUNTA —quien delegó desde el "nunca hice" no tiene ningún texto al que
 * volver—, y contar se deshace volviendo al cuadro con lo escrito adentro.
 */
export function momentoAlDeshacer(momento: MomentoMetaAds): MomentoMetaAds {
  if (momento.paso === "listo" && momento.via === "contado") return { paso: "contame" };
  return MOMENTO_INICIAL;
}
